import { RagIndex } from '@/core/indexesEnum';
import { ModulesEnum, SubModulesEnum } from '@/core/modulesEnum';
import { AuthMethods } from '@/models/auth-methods';
import { UserProducts } from '@/models/user-settings';
import { jwtDecode } from 'jwt-decode';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useAuthStore = defineStore('auth', () => {
  const authMethod = ref<AuthMethods>(
    process.env.VUE_APP_AUTH_METHOD as AuthMethods
  );

  const emptyProducts: UserProducts = {
    modules: {} as Record<ModulesEnum, SubModulesEnum[]>,
    availableIndexes: [],
    displayName: undefined,
    userName: undefined
  };

  const products = ref<UserProducts>(emptyProducts);
  const IsConcurrentUser = ref<boolean>(false);

  const setToken = (newToken: string | undefined): void => {
    const userProducts = loadUserProductsFromToken(newToken);
    setProducts(userProducts);
  };

  const setIsConcurrentUser = (value: boolean): void => {
    IsConcurrentUser.value = value;
  };

  const setProducts = (newProducts: UserProducts): void => {
    products.value = parseProductsModel(newProducts);
  };

  const loadUserProductsFromToken = (tokenValue?: string): UserProducts => {
    if (!tokenValue) {
      return emptyProducts;
    }

    const decoded = jwtDecode<{ products?: string }>(tokenValue);
    const productsClaim = decoded.products;

    if (!productsClaim) {
      return emptyProducts;
    }

    try {
      const parsedProductsString = JSON.parse(productsClaim);
      return parseProductsModel(parsedProductsString);
    } catch (error) {
      console.error('Failed to parse products from token:', error);
      return emptyProducts;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parseProductsModel = (products: UserProducts | any): UserProducts => {
    let parsed: UserProducts;

    if ('Modules' in products && 'AvailableIndexes' in products) {
      parsed = {
        modules: products.Modules,
        availableIndexes: [],
        displayName: products.DisplayName,
        userName: products.UserName
      };
    } else {
      parsed = {
        modules: products.modules ?? {},
        availableIndexes: [],
        displayName: products.displayName,
        userName: products.userName
      };
    }

    parsed.availableIndexes = (
      products.AvailableIndexes ||
      products.availableIndexes ||
      []
    )
      .map((index: string | number) => {
        if (typeof index === 'string' && index in RagIndex) {
          return RagIndex[index as keyof typeof RagIndex];
        } else if (
          typeof index === 'number' &&
          Object.values(RagIndex).includes(index)
        ) {
          return index as RagIndex;
        }
        return null;
      })
      .filter((index: unknown): index is RagIndex => index !== null);

    return parsed;
  };

  return {
    authMethod,
    setToken,
    products,
    setProducts,
    IsConcurrentUser,
    setIsConcurrentUser
  };
});
